import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import { MoneyInput } from './MoneyInput';
import { DateInput } from './DateInput';
import { LookupSelect } from './LookupSelect';
import { ApiError } from '../api/client';
import { useBankAccounts } from '../api/queries/bank-accounts';
import { useRetainers } from '../api/queries/retainers';
import { useRecordInvoicePayment, useUploadPaymentImage } from '../api/queries/invoice-payments';
import { formatMoney } from '../utils/money';
import './RecordPaymentModal.css';

interface Props {
  open: boolean;
  invoiceId: number;
  clientId: number;
  /** Remaining balance as a numeric string — the default payment amount. */
  balanceDue: string;
  onClose: () => void;
}

export function RecordPaymentModal({ open, invoiceId, clientId, balanceDue, onClose }: Props): JSX.Element {
  const bankAccountsQ = useBankAccounts(true);
  const retainersQ = useRetainers(open ? { client_id: clientId } : {});
  const record = useRecordInvoicePayment();
  const uploadImage = useUploadPaymentImage();

  const [amount, setAmount] = useState(balanceDue);
  const [paymentDate, setPaymentDate] = useState<number | null>(null);
  const [bankAccountId, setBankAccountId] = useState<number | ''>('');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [retainerId, setRetainerId] = useState<number | ''>('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [defaulted, setDefaulted] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(balanceDue);
      setPaymentDate(Date.now());
      setBankAccountId('');
      setMethod('');
      setReference('');
      setNotes('');
      setRetainerId('');
      setFiles([]);
      setError(null);
      setDefaulted(false);
    }
  }, [open, balanceDue]);

  const accounts = bankAccountsQ.data?.bank_accounts ?? [];
  const activeRetainers = (retainersQ.data?.retainers ?? []).filter((r) => Number(r.current_balance) > 0);

  // Preselect the default deposit account once the list loads.
  useEffect(() => {
    if (open && !defaulted && accounts.length > 0) {
      const def = accounts.find((a) => a.is_default);
      if (def) setBankAccountId(def.id);
      setDefaulted(true);
    }
  }, [open, defaulted, accounts]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError('Enter a payment amount.');
      return;
    }
    try {
      const res = await record.mutateAsync({
        invoice_id: invoiceId,
        amount,
        payment_date: paymentDate ?? undefined,
        bank_account_id: bankAccountId === '' ? undefined : bankAccountId,
        method: method || undefined,
        reference_number: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        draw_from_retainer_id: retainerId === '' ? undefined : retainerId,
      });
      if (files.length > 0) {
        try {
          await Promise.all(
            files.map((f) =>
              uploadImage.mutateAsync({ invoice_id: invoiceId, payment_id: res.payment.id, file: f }),
            ),
          );
        } catch {
          setError('Payment recorded, but a check image failed to upload. Add it from the payment list.');
          return;
        }
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
        const body = err.body as { error?: string } | null;
        setError(body?.error ?? 'Payment could not be recorded. Refresh and retry.');
        return;
      }
      setError('Could not record the payment. Try again.');
    }
  };

  const busy = record.isPending || uploadImage.isPending;

  return (
    <Modal
      title="Record Payment"
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="record-payment-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Recording…' : 'Record payment'}
          </button>
        </>
      }
    >
      <form id="record-payment-form" className="rpm-form" onSubmit={onSubmit} noValidate>
        <div className="rpm-row">
          <label className="rpm-field">
            <span>Amount *</span>
            <MoneyInput value={amount} onChange={setAmount} />
            <span className="rpm-hint">Balance due: {formatMoney(balanceDue)}</span>
          </label>
          <label className="rpm-field">
            <span>Payment date</span>
            <DateInput value={paymentDate} onChange={setPaymentDate} />
          </label>
        </div>

        <div className="rpm-row">
          <label className="rpm-field rpm-field-grow">
            <span>Deposited to</span>
            <select
              className="form-input"
              value={bankAccountId === '' ? '' : String(bankAccountId)}
              onChange={(e) => setBankAccountId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">— (none) —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}{a.account_last4 ? ` ••••${a.account_last4}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="rpm-field">
            <span>Method</span>
            <LookupSelect category="payment_method" value={method} onChange={setMethod} allowEmpty />
          </label>
        </div>

        <label className="rpm-field">
          <span>Check # / reference</span>
          <input
            type="text"
            className="form-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. 1042"
            maxLength={200}
          />
        </label>

        {activeRetainers.length > 0 && (
          <label className="rpm-field">
            <span>Draw from retainer</span>
            <select
              className="form-input"
              value={retainerId === '' ? '' : String(retainerId)}
              onChange={(e) => setRetainerId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">No retainer draw</option>
              {activeRetainers.map((r) => (
                <option key={r.id} value={r.id}>
                  Retainer #{r.id} ({formatMoney(r.current_balance)})
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="rpm-field">
          <span>Check images</span>
          <input
            type="file"
            className="rpm-file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          {files.length > 0 && (
            <span className="rpm-hint">{files.length} image{files.length > 1 ? 's' : ''} selected</span>
          )}
        </label>

        <label className="rpm-field">
          <span>Notes</span>
          <textarea
            className="form-input rpm-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={10_000}
          />
        </label>

        {error && <p className="rpm-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
